import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { VisualizationService } from '../../services/visualization.service';
import {
  CLUSTERIZE_STAGES,
  CLUSTERS_PAGE_SIZE,
  ClusterChunk,
  ClusterDocument,
  ClusterizeJobStatus,
  ClusterSummary,
  InferenceJobStatus
} from './clusters.types';

@Component({
  selector: 'app-clusters',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './clusters.component.html',
  styleUrl: './clusters.component.css'
})
export class ClustersComponent implements OnInit, OnDestroy {
  @Output() backClick = new EventEmitter<void>();
  @Output() inferencesGenerated = new EventEmitter<void>();

  clusters: ClusterSummary[] = [];
  isLoading = true;
  loadError: string | null = null;

  isClusterizing = false;
  clusterError: string | null = null;
  clusterizeSummary: string | null = null;
  jobStatus: ClusterizeJobStatus | null = null;

  expandedClusterId: number | null = null;
  expandedSourceKeys = new Set<string>();
  expandedChunkKeys = new Set<string>();
  sourceChunksByKey: Record<string, ClusterChunk[]> = {};
  sourceChunksLoading: Record<string, boolean> = {};
  sourceChunksError: Record<string, string | null> = {};
  copiedChunkKey: string | null = null;
  generateSuccess: string | null = null;
  runId: string | null = null;

  generatingClusterId: number | null = null;
  clusterGenerateError: string | null = null;

  page = 1;
  pageSize = CLUSTERS_PAGE_SIZE;
  total = 0;
  totalPages = 0;

  private subscriptions: Subscription[] = [];
  private destroyed = false;
  private static readonly CHUNK_PREVIEW_LENGTH = 280;

  private lastHandledResultRunId: string | null = null;

  constructor(private visualizationService: VisualizationService) {}

  ngOnInit(): void {
    this.loadClusters();

    // Pick up a job for *this* tenant that was started before the app's current
    // lifetime knew about it - e.g. the browser was closed and reopened (or the app
    // was navigated here without ever loading this tenant since startup). The
    // server-side job itself needs no browser/session at all to keep running; this
    // only re-attaches this tab's polling to it. A no-op if already tracking it.
    this.visualizationService.ensureTrackingForCurrentTenant();
    this.visualizationService.ensureInferenceTrackingForCurrentTenant();

    // The job itself is polled by VisualizationService (a root singleton), not by this
    // component, so it keeps running even if this component is destroyed (e.g. the user
    // navigates away and back). This subscription only reflects whatever the service's
    // latest known status is - including a job that finished while we were away.
    const statusSub = this.visualizationService.clusterizeStatus$.subscribe(status => {
      if (!this.destroyed) {
        this.applyJobStatus(status);
      }
    });
    this.subscriptions.push(statusSub);

    const inferenceSub = this.visualizationService.inferenceJobStatus$.subscribe(status => {
      if (!this.destroyed) {
        this.applyInferenceJobStatus(status);
      }
    });
    this.subscriptions.push(inferenceSub);
  }

  private applyInferenceJobStatus(status: InferenceJobStatus | null): void {
    if (!status) {
      this.generatingClusterId = null;
      return;
    }

    if (status.status === 'queued' || status.status === 'running') {
      this.generatingClusterId = status.cluster_id ?? this.generatingClusterId;
      this.clusterGenerateError = null;
      this.generateSuccess = null;
      return;
    }

    if (status.status === 'completed') {
      this.generatingClusterId = null;
      this.clusterGenerateError = null;
      // job_id dedupe lives on the (singleton) service rather than this component so it
      // survives this component being destroyed/recreated - otherwise every re-visit to
      // the Clusters screen would see the BehaviorSubject replay this same "completed"
      // status as if it were new and re-emit inferencesGenerated, forcing the user back
      // to the Inferences screen every time.
      if (status.job_id && !this.visualizationService.hasHandledInferenceJob(status.job_id)) {
        this.visualizationService.markInferenceJobHandled(status.job_id);
        const count = status.result_count ?? 0;
        this.generateSuccess =
          count === 0
            ? 'No inferences found for this cluster.'
            : `Generated ${count} inference${count === 1 ? '' : 's'}.`;
        this.inferencesGenerated.emit();
      }
      return;
    }

    if (status.status === 'failed') {
      this.generatingClusterId = null;
      if (status.job_id && !this.visualizationService.hasHandledInferenceJob(status.job_id)) {
        this.visualizationService.markInferenceJobHandled(status.job_id);
        this.clusterGenerateError =
          status.error || 'Failed to generate inferences for this cluster.';
      }
    }
  }

  private applyJobStatus(status: ClusterizeJobStatus | null): void {
    this.jobStatus = status;

    if (!status) {
      // No tracked job for this tenant (none ever started, or the last one tracked
      // belongs to a different tenant - VisualizationService nulls it out in that
      // case). Clear any summary/error left over from a previous tenant's job rather
      // than leaving it on screen next to this tenant's real (possibly empty) data.
      this.isClusterizing = false;
      this.clusterizeSummary = null;
      this.clusterError = null;
      return;
    }

    this.isClusterizing = status.status === 'queued' || status.status === 'running';

    if (status.status === 'completed') {
      this.clusterError = null;
      const data = status.result;
      if (data) {
        const clusterCount = data.cluster_count ?? 0;
        this.clusterizeSummary = clusterCount === 0
          ? 'No clusters formed — the index may be empty or all chunks were noise.'
          : `Clustered ${data.chunk_count ?? 0} chunks into ${clusterCount} clusters` +
            (data.noise_count ? ` (${data.noise_count} noise)` : '') + '.';
      }
      // A BehaviorSubject replays its last value to every new subscriber (e.g. when this
      // component remounts after the job already finished), so guard the reload/collapse
      // side effects to run once per completed run rather than on every replay.
      if (data?.run_id && data.run_id !== this.lastHandledResultRunId) {
        this.lastHandledResultRunId = data.run_id;
        this.expandedClusterId = null;
        this.clearSourceExpandState(true);
        this.loadClusters(1);
      }
    } else if (status.status === 'failed') {
      this.clusterizeSummary = null;
      this.clusterError = status.error || 'Failed to run clustering. The clustering service may not be configured.';
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.subscriptions.forEach(sub => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });
    this.subscriptions = [];
  }

  get clustersFoundLabel(): string {
    const n = this.total;
    return `${n} cluster${n !== 1 ? 's' : ''} found`;
  }

  get pageRangeLabel(): string {
    if (this.total === 0) {
      return '';
    }
    const start = (this.page - 1) * this.pageSize + 1;
    const end = Math.min(this.page * this.pageSize, this.total);
    return `Showing ${start}–${end} of ${this.total}`;
  }

  get canGoPrev(): boolean {
    return this.page > 1 && !this.isLoading;
  }

  get canGoNext(): boolean {
    return this.page < this.totalPages && !this.isLoading;
  }

  get runIdShort(): string {
    const id = (this.runId || '').trim();
    return id ? id.slice(0, 8) : '';
  }

  // Index of the job's current stage within CLUSTERIZE_STAGES, or -1 before the first
  // stage has been recorded yet (e.g. the job was just queued, or it predates progress
  // tracking). Drives both the fill amount and the "N of 5" label.
  get progressStageIndex(): number {
    const stage = this.jobStatus?.stage;
    return stage ? CLUSTERIZE_STAGES.indexOf(stage) : -1;
  }

  // progress_total === 0 means the current stage can't report a real fraction yet
  // (only "fetching" does this, while it's still scanning) - same for no stage at all.
  get progressIsIndeterminate(): boolean {
    return this.progressStageIndex < 0 || (this.jobStatus?.progress_total ?? 0) === 0;
  }

  // Overall completion across all 5 stages: fully-finished stages plus the current
  // stage's own fraction, e.g. 2 finished stages + embedding 4200/9800 -> 2.43/5 -> ~49%.
  get progressPercent(): number {
    const idx = this.progressStageIndex;
    if (idx < 0) {
      return 0;
    }
    const total = this.jobStatus?.progress_total ?? 0;
    const current = this.jobStatus?.progress_current ?? 0;
    const stageFraction = total > 0 ? Math.min(1, current / total) : 0;
    return Math.min(100, ((idx + stageFraction) / CLUSTERIZE_STAGES.length) * 100);
  }

  get progressPercentRounded(): number {
    return Math.round(this.progressPercent);
  }

  // Deliberately not naming the underlying stage (e.g. "Embedding chunks") - the user
  // just needs to know clustering is progressing, not which internal step it's on.
  get progressStageLabel(): string {
    return 'Clustering in progress…';
  }

  get showProgressBar(): boolean {
    return this.isClusterizing;
  }

  loadClusters(page: number = this.page): void {
    this.isLoading = true;
    this.loadError = null;
    this.page = Math.max(1, page);

    const sub = this.visualizationService.getClusters(this.page, this.pageSize).subscribe({
      next: (res: any) => {
        if (this.destroyed) {
          return;
        }
        const data = res?.data ?? res ?? {};
        this.clusters = this.normalizeClusters(data.clusters ?? []);
        this.runId = data.run_id ?? data.runId ?? data.RunId ?? null;
        this.total = data.total ?? data.Total ?? this.clusters.length;
        this.page = data.page ?? data.Page ?? this.page;
        this.pageSize = data.page_size ?? data.pageSize ?? data.PageSize ?? this.pageSize;
        this.totalPages = data.total_pages ?? data.totalPages ?? data.TotalPages ??
          (this.total ? Math.ceil(this.total / this.pageSize) : 0);
        this.isLoading = false;
      },
      error: (err) => {
        if (this.destroyed) {
          return;
        }
        console.error('Failed to load clusters', err);
        this.isLoading = false;
        this.loadError = this.messageFromError(err, 'Failed to load clusters.');
      }
    });
    this.subscriptions.push(sub);
  }

  goToPrevPage(): void {
    if (!this.canGoPrev) {
      return;
    }
    this.expandedClusterId = null;
    this.clearSourceExpandState();
    this.loadClusters(this.page - 1);
  }

  goToNextPage(): void {
    if (!this.canGoNext) {
      return;
    }
    this.expandedClusterId = null;
    this.clearSourceExpandState();
    this.loadClusters(this.page + 1);
  }

  clusterize(): void {
    if (this.isClusterizing) {
      return;
    }

    this.isClusterizing = true;
    this.clusterError = null;
    this.clusterizeSummary = null;

    // Kicks off a background run on the server and returns almost immediately with a
    // queued/running job status. The ngOnInit subscription to clusterizeStatus$ (backed
    // by polling that lives in VisualizationService, not here) takes it from there, so
    // progress keeps updating even if the user switches screens before it finishes.
    const sub = this.visualizationService.startClusterize().subscribe({
      error: (err) => {
        if (this.destroyed) {
          return;
        }
        console.error('Failed to start clustering', err);
        this.isClusterizing = false;
        this.clusterError = this.messageFromError(
          err,
          'Failed to run clustering. The clustering service may not be configured.'
        );
      }
    });
    this.subscriptions.push(sub);
  }

  toggleCluster(clusterId: number): void {
    if (this.expandedClusterId === clusterId) {
      this.expandedClusterId = null;
    } else {
      this.expandedClusterId = clusterId;
    }
    this.clearSourceExpandState();
  }

  isExpanded(clusterId: number): boolean {
    return this.expandedClusterId === clusterId;
  }

  toggleSource(clusterId: number, documentName: string): void {
    const key = this.sourceKey(clusterId, documentName);
    const next = new Set(this.expandedSourceKeys);
    if (next.has(key)) {
      next.delete(key);
      this.expandedSourceKeys = next;
      return;
    }
    next.add(key);
    this.expandedSourceKeys = next;
    this.loadSourceChunks(clusterId, documentName);
  }

  isSourceExpanded(clusterId: number, documentName: string): boolean {
    return this.expandedSourceKeys.has(this.sourceKey(clusterId, documentName));
  }

  isSourceChunksLoading(clusterId: number, documentName: string): boolean {
    return !!this.sourceChunksLoading[this.sourceKey(clusterId, documentName)];
  }

  sourceChunksErrorFor(clusterId: number, documentName: string): string | null {
    return this.sourceChunksError[this.sourceKey(clusterId, documentName)] ?? null;
  }

  sourceChunksFor(clusterId: number, documentName: string): ClusterChunk[] {
    return this.sourceChunksByKey[this.sourceKey(clusterId, documentName)] ?? [];
  }

  visibleSourceChunks(clusterId: number, documentName: string): ClusterChunk[] | null {
    if (this.isSourceChunksLoading(clusterId, documentName) || this.sourceChunksErrorFor(clusterId, documentName)) {
      return null;
    }
    const chunks = this.sourceChunksFor(clusterId, documentName);
    return chunks.length > 0 ? chunks : null;
  }

  sourceMetaLabel(clusterId: number, doc: ClusterDocument): string {
    const count = doc.chunk_count || 0;
    const chunkWord = count === 1 ? 'chunk' : 'chunks';
    if (this.isSourceExpanded(clusterId, doc.document_name)) {
      const loaded = this.sourceChunksFor(clusterId, doc.document_name);
      if (this.isSourceChunksLoading(clusterId, doc.document_name)) {
        return `Loading ${count} ${chunkWord}…`;
      }
      if (loaded.length > 0) {
        return `${loaded.length} ${loaded.length === 1 ? 'chunk' : 'chunks'} in this cluster`;
      }
    }
    return `${count} ${chunkWord} in this cluster`;
  }

  sourceToggleLabel(clusterId: number, doc: ClusterDocument): string {
    const name = doc.document_name;
    return this.isSourceExpanded(clusterId, name)
      ? `Hide chunks for ${name}`
      : `View chunks for ${name}`;
  }

  chunkKey(clusterId: number, documentName: string, chunk: ClusterChunk, index: number): string {
    return `${this.sourceKey(clusterId, documentName)}::${chunk.chunk_id || index}`;
  }

  isChunkLong(text: string): boolean {
    return (text || '').length > ClustersComponent.CHUNK_PREVIEW_LENGTH;
  }

  isChunkTextExpanded(clusterId: number, documentName: string, chunk: ClusterChunk, index: number): boolean {
    return this.expandedChunkKeys.has(this.chunkKey(clusterId, documentName, chunk, index));
  }

  displayedChunkText(clusterId: number, documentName: string, chunk: ClusterChunk, index: number): string {
    const text = chunk.text_content || '';
    if (!this.isChunkLong(text) || this.isChunkTextExpanded(clusterId, documentName, chunk, index)) {
      return text;
    }
    const cut = text.slice(0, ClustersComponent.CHUNK_PREVIEW_LENGTH);
    const atSpace = cut.lastIndexOf(' ');
    const preview = atSpace > 80 ? cut.slice(0, atSpace) : cut;
    return `${preview.trimEnd()}…`;
  }

  toggleChunkText(clusterId: number, documentName: string, chunk: ClusterChunk, index: number): void {
    const key = this.chunkKey(clusterId, documentName, chunk, index);
    const next = new Set(this.expandedChunkKeys);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.expandedChunkKeys = next;
  }

  retrySourceChunks(clusterId: number, documentName: string): void {
    const key = this.sourceKey(clusterId, documentName);
    const rest = { ...this.sourceChunksByKey };
    delete rest[key];
    this.sourceChunksByKey = rest;
    this.sourceChunksError = { ...this.sourceChunksError, [key]: null };
    this.loadSourceChunks(clusterId, documentName);
  }

  copyChunk(clusterId: number, documentName: string, chunk: ClusterChunk, index: number): void {
    const key = this.chunkKey(clusterId, documentName, chunk, index);
    const text = chunk.text_content || '';
    const copied = () => {
        if (this.destroyed) {
          return;
        }
        this.copiedChunkKey = key;
        window.setTimeout(() => {
          if (!this.destroyed && this.copiedChunkKey === key) {
            this.copiedChunkKey = null;
          }
        }, 1600);
      };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(copied).catch(() => this.copyChunkFallback(text, copied));
      return;
    }
    this.copyChunkFallback(text, copied);
  }

  generateForCluster(cluster: ClusterSummary): void {
    const names = (cluster.documents || [])
      .map(d => (d.document_name || '').trim())
      .filter(Boolean);

    if (names.length === 0 || this.generatingClusterId !== null) {
      return;
    }

    this.generatingClusterId = cluster.cluster_id;
    this.clusterGenerateError = null;
    this.generateSuccess = null;

    const sub = this.visualizationService.generateInferences(names, cluster.cluster_id, this.runId).subscribe({
      next: () => {
        // Job is queued; generatingClusterId stays set until inferenceJobStatus$ reports
        // completed/failed (applyInferenceJobStatus).
      },
      error: (err) => {
        if (this.destroyed) {
          return;
        }
        console.error('Failed to start inference generation for cluster', err);
        this.generatingClusterId = null;
        this.clusterGenerateError = this.messageFromError(
          err,
          'Failed to generate inferences for this cluster.'
        );
      }
    });
    this.subscriptions.push(sub);
  }

  onBackClick(): void {
    this.backClick.emit();
  }

  private sourceKey(clusterId: number, documentName: string): string {
    return `${clusterId}::${documentName}`;
  }

  private clearSourceExpandState(clearCache = false): void {
    this.expandedSourceKeys = new Set();
    this.expandedChunkKeys = new Set();
    this.copiedChunkKey = null;
    this.sourceChunksLoading = {};
    this.sourceChunksError = {};
    if (clearCache) {
      this.sourceChunksByKey = {};
    }
  }

  private copyChunkFallback(text: string, onCopied: () => void): void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      onCopied();
    } finally {
      document.body.removeChild(textarea);
    }
  }

  private loadSourceChunks(clusterId: number, documentName: string): void {
    const key = this.sourceKey(clusterId, documentName);
    if (this.sourceChunksByKey[key] || this.sourceChunksLoading[key]) {
      return;
    }

    this.sourceChunksLoading = { ...this.sourceChunksLoading, [key]: true };
    this.sourceChunksError = { ...this.sourceChunksError, [key]: null };

    const sub = this.visualizationService.getClusterDocumentChunks(clusterId, documentName).subscribe({
      next: (res: any) => {
        if (this.destroyed) {
          return;
        }
        const data = res?.data ?? res ?? {};
        const rawChunks = Array.isArray(data.chunks)
          ? data.chunks
          : Array.isArray(data.Chunks)
            ? data.Chunks
            : [];
        this.sourceChunksByKey = {
          ...this.sourceChunksByKey,
          [key]: this.normalizeChunks(rawChunks)
        };
        this.sourceChunksLoading = { ...this.sourceChunksLoading, [key]: false };
      },
      error: (err) => {
        if (this.destroyed) {
          return;
        }
        console.error('Failed to load cluster document chunks', err);
        this.sourceChunksLoading = { ...this.sourceChunksLoading, [key]: false };
        this.sourceChunksError = {
          ...this.sourceChunksError,
          [key]: this.messageFromError(err, 'Failed to load chunks for this source.')
        };
      }
    });
    this.subscriptions.push(sub);
  }

  private normalizeChunks(raw: any[]): ClusterChunk[] {
    return raw
      .map((chunk: any) => ({
        chunk_id: chunk.chunk_id ?? chunk.chunkId ?? '',
        text_content: (chunk.text_content ?? chunk.textContent ?? '').trim()
      }))
      .filter((chunk: ClusterChunk) => !!chunk.text_content);
  }

  private normalizeClusters(raw: any[]): ClusterSummary[] {
    if (!Array.isArray(raw)) {
      return [];
    }

    return raw.map((c: any) => {
      const documents: ClusterDocument[] = Array.isArray(c.documents)
        ? c.documents
            .map((d: any) => ({
              document_name: d.document_name ?? d.documentName ?? '',
              chunk_count: d.chunk_count ?? d.chunkCount ?? 0
            }))
            .filter((d: ClusterDocument) => !!d.document_name)
        : Array.isArray(c.document_names)
          ? c.document_names
              .filter((n: string) => !!n)
              .map((n: string) => ({ document_name: n, chunk_count: 0 }))
          : [];

      return {
        cluster_id: c.cluster_id ?? c.clusterId ?? 0,
        chunk_count: c.chunk_count ?? c.chunkCount ?? 0,
        documents
      };
    });
  }

  private messageFromError(err: any, fallback: string): string {
    const detail = err?.error?.detail || err?.error?.message || err?.message;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
    if (err?.status === 503) {
      return 'Clustering service is not configured.';
    }
    return fallback;
  }
}
